import {
	faArrowLeft,
	faImage,
	faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ResilientImage, useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ItemDesignationBadges } from '../components/ItemDesignationBadges';
import { PayInvoiceSidebar } from '../components/PayInvoiceSidebar';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import * as portalInvoiceService from '../services/portalInvoiceService';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * Invoice detail page with line items and billing address.
 */
export function InvoiceDetailPage(): JSX.Element {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { token, isAuthenticated } = usePortalAuth();
	const display = useDisplaySettings();
	const queryClient = useQueryClient();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const [showPaySidebar, setShowPaySidebar] = useState(false);

	const invoiceId = id != null ? parseInt(id, 10) : NaN;

	const { data, isLoading } = useQuery({
		queryKey: ['invoice', invoiceId],
		queryFn: () =>
			portalInvoiceService.getInvoiceDetail(token ?? '', invoiceId),
		enabled: token != null && !isNaN(invoiceId),
	});

	const invoice = data?.data ?? null;

	// Prompt login if not authenticated
	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<FontAwesomeIcon
					icon={faSpinner}
					spin
					className="text-2xl text-gray-400"
				/>
			</div>
		);
	}

	if (invoice == null) {
		return (
			<div className="max-w-4xl mx-auto px-4 py-12 text-center">
				<p className="text-brand-muted mb-4">{t('Invoice not found.')}</p>
				<button
					type="button"
					onClick={() => navigate(-1)}
					className="text-sm text-portal-accent hover:underline"
				>
					{t('Back to invoices')}
				</button>
			</div>
		);
	}

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			{/* Back button — pop one history entry rather than push
			    /invoices, so the customer's browser-back doesn't ping-
			    pong between detail and list. */}
			<button
				type="button"
				onClick={() => navigate(-1)}
				className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 min-h-touch mb-4"
			>
				<FontAwesomeIcon icon={faArrowLeft} />
				<span>{t('Back to Invoices')}</span>
			</button>

			{/* Invoice header */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<div className="flex items-start justify-between gap-3">
					<div>
						<div className="flex items-center gap-2">
							<h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
								{t('Invoice')} #{t(invoice.externalId ?? String(invoice.id))}
							</h1>
							{(() => {
								const balance = parseFloat(invoice.outstandingBalance ?? '0');
								const total = parseFloat(invoice.total ?? '0');
								if (balance <= 0) {
									return (
										<span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
											{t('Paid')}
										</span>
									);
								}
								if (balance < total) {
									return (
										<span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300">
											{t('Partial')}
										</span>
									);
								}
								return (
									<span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300">
										{t('Unpaid')}
									</span>
								);
							})()}
						</div>
						{invoice.clientName != null && (
							<p className="text-sm text-brand-muted mt-0.5">
								{t(invoice.clientName)}
							</p>
						)}
						<p className="text-sm text-brand-muted mt-1">
							{t('Date')}: {t(invoice.date ?? '')}
							{invoice.dueDate != null && (
								<span className="ml-3">
									{t('Due')}: {t(invoice.dueDate)}
								</span>
							)}
						</p>
						{invoice.paymentTerm != null && (
							<p className="text-xs text-gray-400 mt-0.5">
								{t('Terms')}: {t(invoice.paymentTerm)}
							</p>
						)}
					</div>
					<div className="text-right shrink-0">
						<div className="text-xl font-bold text-gray-900 dark:text-gray-100">
							{t(formatCurrency(invoice.total) ?? '-')}
						</div>
						{invoice.outstandingBalance != null &&
							parseFloat(invoice.outstandingBalance) > 0 && (
								<>
									<div className="text-sm text-red-600 dark:text-red-400 mt-0.5">
										{t('Balance')}:{' '}
										{t(formatCurrency(invoice.outstandingBalance) ?? '')}
									</div>
									{/* Pay button hidden when the storefront disables
									    payments OR no gateway is configured (the API
									    coerces canPayInvoices to false in either
									    case). Without this gate the user could open
									    the sidebar, fill it out, and then hit a 503 — a
									    worse experience than no button at all. */}
									{display.canPayInvoices && (
										<button
											type="button"
											onClick={() => setShowPaySidebar(true)}
											className="mt-2 px-4 py-1.5 bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-medium rounded-lg transition-colors"
										>
											{t('Pay Invoice')}
										</button>
									)}
								</>
							)}
					</div>
				</div>
			</div>

			{/* Summary card */}
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				<h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
					{t('Summary')}
				</h2>
				<dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
					<dt className="text-brand-muted">{t('Subtotal')}</dt>
					<dd className="text-right text-gray-900 dark:text-gray-100">
						{t(formatCurrency(invoice.subtotal) ?? '-')}
					</dd>

					{invoice.discount != null && parseFloat(invoice.discount) !== 0 && (
						<>
							<dt className="text-brand-muted">{t('Discount')}</dt>
							<dd className="text-right text-green-600 dark:text-green-400">
								-{t(formatCurrency(invoice.discount) ?? '')}
							</dd>
						</>
					)}

					{invoice.tax != null && parseFloat(invoice.tax) !== 0 && (
						<>
							<dt className="text-brand-muted">{t('Tax')}</dt>
							<dd className="text-right text-gray-900 dark:text-gray-100">
								{t(formatCurrency(invoice.tax) ?? '')}
							</dd>
						</>
					)}

					{invoice.freight != null && parseFloat(invoice.freight) !== 0 && (
						<>
							<dt className="text-brand-muted">{t('Shipping')}</dt>
							<dd className="text-right text-gray-900 dark:text-gray-100">
								{t(formatCurrency(invoice.freight) ?? '')}
							</dd>
						</>
					)}

					<dt className="text-gray-900 dark:text-gray-100 font-semibold border-t border-brand-border pt-2">
						{t('Total')}
					</dt>
					<dd className="text-right text-gray-900 dark:text-gray-100 font-bold border-t border-brand-border pt-2">
						{t(formatCurrency(invoice.total) ?? '-')}
					</dd>
				</dl>
			</div>

			{/* Billing address */}
			{invoice.billingAddress != null && (
				<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
					<h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
						{t('Billing Address')}
					</h2>
					<div className="text-sm text-gray-600 dark:text-gray-400">
						{invoice.billingAddress.name != null && (
							<p>{t(invoice.billingAddress.name)}</p>
						)}
						{invoice.billingAddress.line1 != null && (
							<p>{t(invoice.billingAddress.line1)}</p>
						)}
						{invoice.billingAddress.line2 != null && (
							<p>{t(invoice.billingAddress.line2)}</p>
						)}
						<p>
							{[invoice.billingAddress.city, invoice.billingAddress.state]
								.filter((v): v is string => v != null)
								.map((v) => t(v))
								.join(', ')}
							{invoice.billingAddress.zip != null &&
								` ${t(invoice.billingAddress.zip)}`}
						</p>
					</div>
				</div>
			)}

			{/* Line items */}
			<div className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden">
				<h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 px-4 py-3 border-b border-brand-border">
					{t('Line Items')} ({t(String(invoice.lineItems.length))})
				</h2>
				<div className="divide-y divide-gray-100 dark:divide-gray-800">
					{invoice.lineItems.map((line) => (
						<button
							key={line.id}
							type="button"
							onClick={() => {
								if (line.fkItemId != null) {
									navigate(`/product/${line.fkItemId}`);
								}
							}}
							className={`w-full text-left px-4 py-3 flex items-start gap-3 ${
								line.fkItemId != null
									? 'hover:bg-brand-input transition-colors'
									: ''
							}`}
						>
							{/* Thumbnail — same treatment as the order detail
							    line. Falls back to a generic image icon
							    when the item has no picture (or the line is
							    a non-item ad-hoc entry like a freight
							    surcharge); the row width still aligns with
							    rows that DO have pictures so the column
							    edges read cleanly. */}
							<div className="w-14 h-14 shrink-0 bg-brand-input rounded-lg flex items-center justify-center overflow-hidden">
								<ResilientImage
									src={line.primaryImageUrl}
									alt={t(line.description ?? '')}
									className="w-full h-full object-cover"
									onLikelyStaleUrl={() => {
										void queryClient.invalidateQueries({
											queryKey: ['invoice', invoiceId],
										});
									}}
									fallback={
										<FontAwesomeIcon
											icon={faImage}
											className="text-gray-400 dark:text-gray-500 text-lg"
											aria-label={t('No image available')}
										/>
									}
								/>
							</div>
							<div className="flex-1 min-w-0">
								<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
									{t(line.description ?? '')}
								</p>
								{line.itemExternalId != null && (
									<p className="text-xs text-gray-400 mt-0.5">
										{t('Item')} #{t(line.itemExternalId)}
									</p>
								)}
								{line.designations != null && (
									<div className="mt-0.5">
										<ItemDesignationBadges designations={line.designations} />
									</div>
								)}
								<p className="text-xs text-brand-muted mt-0.5">
									{t('Qty')}: {t(String(line.quantity ?? 0))} ×{' '}
									{t(formatCurrency(line.price) ?? '-')}
								</p>
							</div>
							<div className="text-sm font-medium text-gray-900 dark:text-gray-100 shrink-0">
								{t(formatCurrency(line.total) ?? '-')}
							</div>
						</button>
					))}
				</div>
			</div>

			{/* Pay invoice sidebar */}
			{invoice.outstandingBalance != null &&
				parseFloat(invoice.outstandingBalance) > 0 && (
					<PayInvoiceSidebar
						isOpen={showPaySidebar}
						onClose={() => setShowPaySidebar(false)}
						invoiceId={invoice.id}
						outstandingBalance={parseFloat(invoice.outstandingBalance)}
						invoiceExternalId={invoice.externalId}
						onPaymentSuccess={() => {
							setShowPaySidebar(false);
							// Re-fetch invoice detail to get updated balance and status
							void queryClient.invalidateQueries({
								queryKey: ['invoice', invoiceId],
							});
						}}
					/>
				)}
		</div>
	);
}
